import { z } from 'zod';
import { updateStoreDetailsSchema } from '../schemas/store-details';
import type { SupabaseClient } from '../supabase/client';
import type {
  StoreDetailsErrorKey,
  StoreLogoChange,
  UpdateStoreDetailsInput,
  UpdateStoreDetailsResult,
} from '../types/store';
import {
  assertStagedStoreLogoForStore,
  getSupabaseUrlFromUnknown,
} from '../lib/store-logo-storage';
import { getAdminStore } from './get-admin-store';

const ALLOWED_INPUT_KEYS = new Set(['storeId', 'name', 'description', 'localityId', 'logoChange']);

export async function updateStoreDetails(
  client: SupabaseClient,
  input: UpdateStoreDetailsInput,
): Promise<UpdateStoreDetailsResult> {
  assertNoUnexpectedKeys(input);
  const parsed = updateStoreDetailsSchema.parse({
    name: input.name,
    description: input.description,
    localityId: input.localityId,
  });
  const logoUrl = getLogoUrl(input.storeId, input.logoChange, getSupabaseUrlFromUnknown(client));

  const patch: {
    name: string;
    description: string;
    locality_id: string;
    logo_url?: string | null;
  } = {
    name: parsed.name,
    description: parsed.description,
    locality_id: parsed.localityId,
  };
  if (logoUrl !== undefined) patch.logo_url = logoUrl;

  const { data, error } = await client
    .from('stores')
    .update(patch)
    .eq('id', input.storeId)
    .select('id')
    .single();

  if (error) throw error;
  if (!data) throw new Error('updateStoreDetails: empty update result');
  return getAdminStore(client, input.storeId);
}

export function mapStoreDetailsError(err: unknown): StoreDetailsErrorKey {
  if (err instanceof z.ZodError) {
    const message = err.issues[0]?.message;
    if (isStoreDetailsErrorKey(message)) return message;
  }
  if (!err || typeof err !== 'object') return 'common.errors.unknown';

  const maybe = err as { code?: string; message?: string; name?: string; status?: number };

  if (err instanceof TypeError || maybe.name === 'TypeError') return 'common.errors.networkFailed';
  if (maybe.name === 'AuthRetryableFetchError') return 'common.errors.networkFailed';
  if (/\b(fetch|network|abort(?:ed)?)\b|load failed/i.test(maybe.message ?? '')) {
    return 'common.errors.networkFailed';
  }
  if (maybe.code === '42501' || /permission denied/i.test(maybe.message ?? '')) {
    return 'common.errors.permissionDenied';
  }
  if (maybe.code === 'PGRST116' || maybe.status === 406) return 'common.errors.storeNotFound';
  if (/too large/i.test(maybe.message ?? '')) return 'admin.storeSettings.errors.logoTooLarge';
  if (/content type|mime|not allowed/i.test(maybe.message ?? '')) {
    return 'admin.storeSettings.errors.logoInvalidType';
  }
  return 'common.errors.unknown';
}

function assertNoUnexpectedKeys(input: UpdateStoreDetailsInput): void {
  for (const key of Object.keys(input)) {
    if (!ALLOWED_INPUT_KEYS.has(key)) throw new Error(`Unexpected store details input: ${key}`);
  }
}

function getLogoUrl(
  storeId: string,
  logoChange: StoreLogoChange,
  supabaseUrl: string,
): string | null | undefined {
  switch (logoChange.kind) {
    case 'keep':
      return undefined;
    case 'remove':
      return null;
    case 'replace':
      return assertStagedStoreLogoForStore(storeId, logoChange.staged, supabaseUrl).publicUrl;
  }
}

function isStoreDetailsErrorKey(value: unknown): value is StoreDetailsErrorKey {
  return (
    value === 'admin.storeSettings.errors.nameRequired' ||
    value === 'admin.storeSettings.errors.nameTooLong' ||
    value === 'admin.storeSettings.errors.descriptionRequired' ||
    value === 'admin.storeSettings.errors.descriptionTooLong' ||
    value === 'admin.storeSettings.errors.localityRequired'
  );
}
